import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Upload, Download, AlertCircle, XCircle, CheckCircle } from 'lucide-react'
import { FormEvent, useRef, useState } from 'react'

interface PreviewRow {
  row_number: number
  agent_identifier: string
  carrier_code: string
  policy_number: string
  commission_type: string
  gross_amount: string
  agent_amount: string
  status: 'valid' | 'error'
  message: string | null
}

interface ImportResult {
  file_name: string
  total_rows: number
  valid_count: number
  error_count: number
  rows: PreviewRow[]
}

interface Props extends PageProps {
  columns: string[]
  required_columns: string[]
  example_row: string[]
  allowed_commission_types: string[]
  preview: ImportResult | null
}

export default function CommissionsImport({
  columns,
  required_columns,
  example_row,
  allowed_commission_types,
  preview
}: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const submit = (confirm: boolean) => {
    if (!file) return
    setProcessing(true)
    const formData = new FormData()
    formData.append('file', file)
    if (confirm) formData.append('confirm', '1')
    router.post('/commissions/upload', formData, {
      forceFormData: true,
      onFinish: () => setProcessing(false),
      preserveScroll: true
    })
  }

  const handlePreview = (e: FormEvent) => {
    e.preventDefault()
    submit(false)
  }

  const handleConfirm = () => submit(true)

  const handleStartOver = () => {
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    router.visit('/commissions/import')
  }

  const statusBadge = (status: PreviewRow['status']) => {
    const cls = status === 'valid'
      ? 'bg-green-100 text-green-700'
      : 'bg-red-100 text-red-700'
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${cls}`}>{status}</span>
  }

  return (
    <AppLayout>
      <Head title="Import Commissions" />

      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/commissions" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Import Commissions</h1>
            <p className="text-chestnut-charcoal/70 mt-1">
              Bulk-create commissions from a CSV file.
            </p>
          </div>
        </div>

        {preview ? (
          <div className="card p-6 space-y-4">
            <div className="flex items-center gap-3">
              {preview.error_count === 0
                ? <CheckCircle className="h-8 w-8 text-green-600" />
                : <AlertCircle className="h-8 w-8 text-red-600" />}
              <div>
                <h2 className="text-xl font-semibold text-chestnut-charcoal">
                  Preview: {preview.file_name}
                </h2>
                <p className="text-sm text-chestnut-charcoal/70">
                  {preview.error_count === 0
                    ? 'All rows look good. Confirm to create commissions.'
                    : 'Fix the rows below and re-upload. No commissions will be created until every row is valid.'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card border-l-4 border-l-green-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Valid</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{preview.valid_count}</div>
              </div>
              <div className="card border-l-4 border-l-red-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Errors</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{preview.error_count}</div>
              </div>
              <div className="card border-l-4 border-l-chestnut-forest p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Total rows</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{preview.total_rows}</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-chestnut-gray text-left text-chestnut-charcoal/70">
                    <th className="py-2 pr-3">Row</th>
                    <th className="py-2 pr-3">Agent</th>
                    <th className="py-2 pr-3">Carrier</th>
                    <th className="py-2 pr-3">Policy</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3 text-right">Gross</th>
                    <th className="py-2 pr-3 text-right">Agent Amt</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row_number} className="border-b border-chestnut-gray/40 align-top">
                      <td className="py-2 pr-3 text-chestnut-charcoal/70">{r.row_number}</td>
                      <td className="py-2 pr-3">{r.agent_identifier || '—'}</td>
                      <td className="py-2 pr-3">{r.carrier_code || '—'}</td>
                      <td className="py-2 pr-3">{r.policy_number || '—'}</td>
                      <td className="py-2 pr-3">{r.commission_type || '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r.gross_amount || '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r.agent_amount || '—'}</td>
                      <td className="py-2 pr-3">{statusBadge(r.status)}</td>
                      <td className="py-2 pr-3 text-chestnut-charcoal/70">
                        {r.message ? (
                          <span className="text-red-700 inline-flex items-start gap-1">
                            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span>{r.message}</span>
                          </span>
                        ) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3 pt-4 border-t border-chestnut-gray">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={processing || preview.error_count > 0 || preview.valid_count === 0 || !file}
                title={
                  preview.error_count > 0
                    ? 'Fix the errors and re-upload first'
                    : !file
                      ? 'Re-select the same file to confirm'
                      : ''
                }
                className="btn-primary"
              >
                {processing
                  ? 'Importing...'
                  : `Confirm & import ${preview.valid_count} commission${preview.valid_count === 1 ? '' : 's'}`}
              </button>
              <button type="button" onClick={handleStartOver} className="btn-secondary" disabled={processing}>
                Cancel / choose another file
              </button>
            </div>

            {!file && (
              <p className="text-xs text-chestnut-charcoal/60">
                Tip: re-select the same CSV above to enable the Confirm button.
              </p>
            )}

            {!file && (
              <form onSubmit={handlePreview} className="pt-4 border-t border-chestnut-gray space-y-3">
                <label className="block text-sm font-semibold text-chestnut-charcoal">
                  Re-select file to confirm
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="input-field w-full"
                />
              </form>
            )}
          </div>
        ) : (
          <>
            <div className="card p-6 space-y-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal">Expected file format</h2>
              <p className="text-sm text-chestnut-charcoal/70">
                Upload a <code>.csv</code> with a header row in the first row. Agents are looked up by their{' '}
                <strong>NPN number</strong> or <strong>email</strong> (whichever you put in
                <code> agent_identifier</code>); carriers are looked up by their <strong>code</strong>.
              </p>
              <div>
                <h3 className="text-sm font-semibold text-chestnut-charcoal mb-2">Required columns</h3>
                <div className="flex flex-wrap gap-2">
                  {required_columns.map((c) => (
                    <span key={c} className="px-2 py-1 text-xs font-mono bg-chestnut-forest/10 text-chestnut-forest rounded">{c}</span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-chestnut-charcoal mb-2">Optional columns</h3>
                <div className="flex flex-wrap gap-2">
                  {columns.filter((c) => !required_columns.includes(c)).map((c) => (
                    <span key={c} className="px-2 py-1 text-xs font-mono bg-chestnut-gray/30 text-chestnut-charcoal rounded">{c}</span>
                  ))}
                </div>
              </div>
              <ul className="text-sm text-chestnut-charcoal/70 list-disc pl-6 space-y-1">
                <li><code>commission_type</code> must be one of: {allowed_commission_types.join(', ')}.</li>
                <li>Dates use <code>YYYY-MM-DD</code>. Money fields accept plain decimals (e.g. <code>1000.00</code>).</li>
                <li>Imports are all-or-nothing — if any row has an error, no commissions are created.</li>
                <li>Override calculations and equity credits run automatically for each imported row.</li>
              </ul>
              <a href="/commissions/import_template" className="btn-secondary inline-flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download CSV template
              </a>
              <p className="text-xs text-chestnut-charcoal/60">
                Example row in template: <code>{example_row.join(', ')}</code>
              </p>
            </div>

            <form onSubmit={handlePreview} className="card p-6 space-y-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal">Upload file</h2>
              <div>
                <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                  CSV file
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="input-field w-full"
                  required
                />
                {file && (
                  <p className="mt-1 text-xs text-chestnut-charcoal/60">Selected: {file.name}</p>
                )}
              </div>
              <div className="flex gap-3 pt-4 border-t border-chestnut-gray">
                <button type="submit" disabled={processing || !file} className="btn-primary inline-flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  {processing ? 'Parsing...' : 'Preview import'}
                </button>
                <Link href="/commissions" className="btn-secondary">Cancel</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </AppLayout>
  )
}
